/**
 * @fileoverview Client-side MongoDB ObjectId generator (24-hex string).
 * @module local-db/helpers
 *
 * Responsibilities:
 * - Generate spec-compliant 12-byte MongoDB ObjectId hex strings on the
 *   client, so local-first records carry the same ID shape as server-written
 *   records (no temp IDs, no remapping).
 *
 * Dependencies:
 * - Web Crypto (crypto.getRandomValues) \u2014 available in all supported
 *   browsers and in the Service Worker runtime.
 *
 * Architecture:
 * - Pure helper. No I/O, no storage. Safe to import from any Dexie service.
 * - Mirrors the shape used by the mobile-pwa helper
 *   (@mobile/helpers/safeUuid.generateObjectId) so both frontends produce
 *   identical ID layouts.
 *
 * Notes:
 * - Spec: 4-byte Unix timestamp + 5-byte random machine id + 3-byte counter.
 * - The machine-id bytes are generated once per module load; the counter is
 *   seeded randomly and monotonically increments. This matches the BSON
 *   ObjectId spec closely enough for cross-device uniqueness within a
 *   typical workload without requiring the `bson` package.
 */

// ---------------------------------------------------------------------------
// Module state: machine-id random bytes (stable for the life of the tab) and
// a monotonically incrementing counter seeded from Math.random().
// ---------------------------------------------------------------------------
let _oidCounter = Math.floor(Math.random() * 0xffffff);

const _oidRandom = (() => {
  const buf = new Uint8Array(5);
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : (globalThis as any).crypto;
  cryptoObj.getRandomValues(buf);
  return buf;
})();

/**
 * @contract generateObjectId
 *
 * Purpose:
 * - Returns a new 24-character lowercase hex string that matches the shape
 *   of a MongoDB ObjectId. Suitable for use as a `_id` value on documents
 *   persisted locally and replicated to MongoDB via sync.
 *
 * Fields:
 * - (return) string \u2014 24 hex characters.
 *
 * Rules:
 * - Safe to call from any browser or Service Worker context.
 * - Each call returns a distinct value within the same runtime.
 *
 * Sync:
 * - Because the shape matches server-generated ObjectIds, records can be
 *   pushed through the sync layer without any ID rewrite on the server.
 */
export function generateObjectId(): string {
  const now = Math.floor(Date.now() / 1000);
  _oidCounter = (_oidCounter + 1) & 0xffffff;
  const ts = now.toString(16).padStart(8, '0');
  const rnd = Array.from(_oidRandom).map((b) => b.toString(16).padStart(2, '0')).join('');
  const cnt = _oidCounter.toString(16).padStart(6, '0');
  return ts + rnd + cnt;
}

/**
 * @contract isObjectIdHex
 *
 * Purpose:
 * - Predicate for validating that a string looks like a MongoDB ObjectId
 *   (24 lowercase hex characters). Use this instead of `ObjectId.isValid`
 *   which is a server-only import.
 */
export function isObjectIdHex(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{24}$/.test(value);
}
